import { Report } from "../../report/models/report.model";
import { State } from "../../report/models/state.model";
import { LineLiveCadence } from "../models/live-production.model";

/** Clés AWS / Siemens utilisées sur le pilotage machine (dashboard-reports). */
export const KEYS = {
  CADENCE_INST: "AM..4:1-1",
  UNITES_PRODUITES: "V..6:32-1",
  TEMPS_FONCTIONNEMENT_S: "V..6:44-1",
  TEMPS_ARRET_S: "V..6:38-1",
  DUREE_SANS_ARRET_S: "V..6:8-1",
  DUREE_MAX_SANS_ARRET_S: "AM..4:9-1",
  DEFAUT_1: "V..6:12-1",
  DEFAUT_2: "V..6:16-1",
  DEFAUT_3: "V..6:20-1",
  DEFAUT_4: "V..6:24-1",
} as const;

/**
 * Si une valeur dépasse 100 000 on suppose des millisecondes (artefact PLC),
 * sinon on prend la valeur en secondes.
 */
function secsFromMaybeMs(n: number | null | undefined): number | null {
  if (n == null) return null;
  if (Math.abs(n) > 100_000) return n / 1000;
  return n;
}

/**
 * Cherche la valeur numérique d'une mesure par clé device.
 * Résout via `measure.deviceInput?.key` (si peuplé) OU via lookup
 * `deviceInputId → key` depuis `report.device.inputs` (comme dashboard-report).
 */
function findValue(
  state: State | undefined,
  key: string,
  inputKeyMap: Map<string, string>,
): number | null {
  if (!state?.measures) return null;
  const m = state.measures.find(
    (x) =>
      x.deviceInput?.key === key ||
      (x.deviceInputId && inputKeyMap.get(x.deviceInputId) === key),
  );
  if (!m || m.value == null) return null;
  const n = Number(m.value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Construit un `LineLiveCadence` depuis un `Report`. Utilise `currentState`
 * en priorité, puis le dernier élément de `states` si nécessaire. Calcule
 * la cadence moyenne `(unites / (marche + arret)) * 3600`.
 *
 * Résout les clés device via `report.device.inputs` pour supporter les
 * Reports où `measure.deviceInput` n'est pas peuplé (cas de l'API Aristote
 * en GET direct — cf. dashboard-report qui fait la même jointure).
 */
export function buildCadenceFromReport(
  report: Report,
  previousHistory: { ts: string; value: number }[] = [],
): LineLiveCadence {
  const state =
    report.currentState ??
    (report.states && report.states.length > 0
      ? report.states[report.states.length - 1]
      : undefined);

  // Build deviceInputId → key lookup from report.device.inputs
  const inputKeyMap = new Map<string, string>();
  if (report.device?.inputs) {
    for (const di of report.device.inputs) {
      if (di.id && di.key) inputKeyMap.set(di.id, di.key);
    }
  }

  const cadenceInst = findValue(state, KEYS.CADENCE_INST, inputKeyMap);
  const unitesProduites = findValue(state, KEYS.UNITES_PRODUITES, inputKeyMap);
  const tempsMarche = secsFromMaybeMs(findValue(state, KEYS.TEMPS_FONCTIONNEMENT_S, inputKeyMap));
  const tempsArret = secsFromMaybeMs(findValue(state, KEYS.TEMPS_ARRET_S, inputKeyMap));
  const dureeSansArret = secsFromMaybeMs(findValue(state, KEYS.DUREE_SANS_ARRET_S, inputKeyMap));
  const dureeMaxSansArret = secsFromMaybeMs(findValue(state, KEYS.DUREE_MAX_SANS_ARRET_S, inputKeyMap));

  let cadenceMoyenneUH: number | null = null;
  if (
    unitesProduites != null &&
    tempsMarche != null &&
    tempsArret != null &&
    tempsMarche + tempsArret >= 60
  ) {
    cadenceMoyenneUH = Math.round(
      (unitesProduites / (tempsMarche + tempsArret)) * 3600,
    );
  }

  const d1 = findValue(state, KEYS.DEFAUT_1, inputKeyMap);
  const d2 = findValue(state, KEYS.DEFAUT_2, inputKeyMap);
  const d3 = findValue(state, KEYS.DEFAUT_3, inputKeyMap);
  const d4 = findValue(state, KEYS.DEFAUT_4, inputKeyMap);
  const nbArrets = (d1 ?? 0) + (d2 ?? 0) + (d3 ?? 0) + (d4 ?? 0);

  const ts = state?.date ? new Date(state.date).toISOString() : new Date().toISOString();

  // Append à l'historique si on a une valeur de cadence instantanée
  const history =
    cadenceInst != null
      ? [...previousHistory, { ts, value: cadenceInst }].slice(-60)
      : previousHistory;

  return {
    cadenceInstantUH: cadenceInst,
    cadenceMoyenneUH,
    unitesProduites,
    dureeSansArretSec: dureeSansArret,
    dureeMaxSansArretSec: dureeMaxSansArret,
    tempsTotalFonctionnementSec: tempsMarche,
    dureeTotaleArretSec: tempsArret,
    nbArrets: nbArrets || null,
    defauts: { d1, d2, d3, d4 },
    lastTickAt: ts,
    history,
  };
}
